import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/Edit';
import FaceIcon from '@material-ui/icons/Face';
import { getFullName, getInitials } from 'utilities/name-utils';
import Pin from '../../@common/Pin';
import Fab from '../../@common/Fab';
import { User } from 'types/schema-generated-types';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: { padding: theme.spacing(3) },
    avatar: {
      height: theme.spacing(16),
      width: theme.spacing(16),
      fontSize: theme.typography.h1.fontSize,
      [theme.breakpoints.down('xs')]: {
        height: theme.spacing(12),
        width: theme.spacing(12)
      }
    },
    actionsContainer: {
      alignSelf: 'center',
      textAlign: 'right'
    },
    detailContainer: {
      marginLeft: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    editButton: {
      marginTop: theme.spacing(1)
    },
    faceButton: {
      [theme.breakpoints.down('xs')]: {
        height: 30,
        width: 30,
        minWidth: 30
      }
    },
    faceIcon: {
      height: 28,
      width: 28,
      [theme.breakpoints.down('xs')]: {
        height: 18,
        width: 18
      }
    }
  };
});

interface UserProfileInformationProps {
  user: User;
  onEditProfile: () => void;
}

const UserProfileInformation: React.FC<UserProfileInformationProps> = ({ user, onEditProfile }) => {
  const classes = useStyles();
  const { t } = useTranslation('UserProfileInformation');

  const fullName = getFullName(user);

  return (
    <Paper className={classes.root} elevation={3}>
      <Grid container spacing={2}>
        <Grid item>
          <Badge
            overlap="circle"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            badgeContent={
              <Fab
                variant="contained"
                color="secondary"
                startIcon={<FaceIcon className={classes.faceIcon} />}
                onClick={onEditProfile}
                className={classes.faceButton}
              />
            }
          >
            <Avatar alt={fullName} src={user.avatarUrl ?? ''} className={classes.avatar}>
              {getInitials(user)}
            </Avatar>
          </Badge>
        </Grid>
        <Grid item>
          <Typography variant="h2">{t('Hi, {{fullName}}', { fullName })}</Typography>
          <Typography variant="body1" color="textSecondary">
            {user.username}
          </Typography>
          <Pin pin={user.pin} />
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            aria-label={t('Edit Profile')}
            startIcon={<EditIcon />}
            onClick={onEditProfile}
            className={classes.editButton}
          >
            <span>{t('Edit Profile')}</span>
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserProfileInformation;
